import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/UserManagement.css';
import usuariosApiService from '../services/usuariosApiService';
import authService from '../services/authService';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });

  const [errors, setErrors] = useState({});

  // Criterios de contraseña segura
  const passwordCriteria = [
    { id: 'length', text: 'Al menos 8 caracteres', check: (pwd) => pwd.length >= 8 },
    { id: 'uppercase', text: 'Al menos una letra mayúscula', check: (pwd) => /[A-Z]/.test(pwd) },
    { id: 'lowercase', text: 'Al menos una letra minúscula', check: (pwd) => /[a-z]/.test(pwd) },
    { id: 'number', text: 'Al menos un número', check: (pwd) => /\d/.test(pwd) },
    { id: 'special', text: 'Al menos un carácter especial (@, #, $, %, etc.)', check: (pwd) => /[@#$%^&*(),.?":{}|<>]/.test(pwd) }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validar en tiempo real
    if (name === 'newPassword') {
      checkPasswordStrength(value);
    }

    // Limpiar errores al escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const checkPasswordStrength = (password) => {
    const passedCriteria = passwordCriteria.filter(criteria => criteria.check(password));
    const score = passedCriteria.length;
    
    let feedback = [];
    if (score < 3) {
      feedback.push('Contraseña débil');
    } else if (score < 5) {
      feedback.push('Contraseña moderada');
    } else {
      feedback.push('Contraseña fuerte');
    }

    setPasswordStrength({ score, feedback });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (passwordStrength.score < 4) {
      newErrors.newPassword = 'La contraseña no cumple con los requisitos mínimos';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Debe confirmar la nueva contraseña';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña debe ser diferente a la actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      (async () => {
        try {
          const user = authService.getCurrentUser();
          if (!user || !user.usuarioId) {
            alert('Usuario no encontrado en sesión');
            return;
          }

          await usuariosApiService.cambiarContrasenia(
            user.usuarioId,
            formData.currentPassword,
            formData.newPassword,
            formData.confirmPassword
          );

          alert('Contraseña cambiada exitosamente');

          // Limpiar formulario
          setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setPasswordStrength({ score: 0, feedback: [] });

          // Opcional: forzar logout para que vuelva a iniciar sesión con la nueva contraseña
          authService.logout();
          navigate('/login');
        } catch (err) {
          console.error('Error en cambiar contraseña:', err);
          const message = err.response?.data?.error || err.message || 'Error al cambiar contraseña';
          alert(message);
        }
      })();
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const getStrengthColor = (score) => {
    if (score < 3) return '#e74c3c';
    if (score < 5) return '#f39c12';
    return '#27ae60';
  };

  const getStrengthWidth = (score) => {
    return `${(score / 5) * 100}%`;
  };

  return (
    <div className="change-password-container">
      <div className="change-password-header">
        <h1>Cambiar Contraseña</h1>
        <p>Actualice su contraseña para mantener su cuenta segura</p>
      </div>

      <div className="password-form-container">
        <form onSubmit={handleSubmit} className="password-form">
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="currentPassword">Contraseña Actual *</label>
              <div className="password-input-group">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Ingrese su contraseña actual"
                  className={errors.currentPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  <i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {errors.currentPassword && (
                <span className="error-message">{errors.currentPassword}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Nueva Contraseña *</label>
              <div className="password-input-group">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Ingrese su nueva contraseña"
                  className={errors.newPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {errors.newPassword && (
                <span className="error-message">{errors.newPassword}</span>
              )}

              {formData.newPassword && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div 
                      className="strength-fill"
                      style={{
                        width: getStrengthWidth(passwordStrength.score),
                        backgroundColor: getStrengthColor(passwordStrength.score)
                      }}
                    ></div>
                  </div>
                  <span 
                    className="strength-text"
                    style={{ color: getStrengthColor(passwordStrength.score) }}
                  >
                    {passwordStrength.feedback[0]}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Nueva Contraseña *</label>
              <div className="password-input-group">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirme su nueva contraseña"
                  className={errors.confirmPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <div className="password-requirements">
            <h4>Requisitos de la contraseña:</h4>
            <div className="requirements-list">
              {passwordCriteria.map(criteria => (
                <div 
                  key={criteria.id} 
                  className={`requirement-item ${
                    formData.newPassword && criteria.check(formData.newPassword) ? 'met' : ''
                  }`}
                >
                  <i className={`fas ${
                    formData.newPassword && criteria.check(formData.newPassword) 
                      ? 'fa-check-circle' 
                      : 'fa-times-circle'
                  }`}></i>
                  <span>{criteria.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="security-tips">
            <h4><i className="fas fa-shield-alt"></i> Consejos de Seguridad</h4>
            <ul>
              <li>Use una combinación única de letras, números y símbolos</li>
              <li>Evite usar información personal como fechas de nacimiento o nombres</li>
              <li>No reutilice contraseñas de otras cuentas</li>
              <li>Considere usar un administrador de contraseñas</li>
            </ul>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              <i className="fas fa-times"></i>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={passwordStrength.score < 4 || Object.keys(errors).length > 0}
            >
              <i className="fas fa-save"></i>
              Cambiar Contraseña
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
